
import React from 'react';
import { Play, Pause } from 'lucide-react';
import espectacularImg from '../assets/images/productos/Portada1.png';
import espectacularImg2 from '../assets/images/productos/Portada2.png';
import espectacularImg3 from '../assets/images/productos/Portada3.png';
import font from '../assets/fonts/Brokenbrush.ttf';
import { ReactComponent as IcfLogoRaw } from '../assets/images/logos/LogoSVG.svg';

// Logo animado con contorno y relleno (copiado de header.js)
const IcfLogo = (props) => {
  const logoRef = React.useRef(null);
  const timeoutIds = React.useRef([]);

  // Función para animar el logo
  const animateLogo = () => {
    const svg = logoRef.current;
    if (!svg) return;
    const paths = svg.querySelectorAll('path');
    paths.forEach((path) => {
      path.style.stroke = '#fff';
      path.style.strokeWidth = '0.7';
      path.style.fillOpacity = '0';
      path.style.strokeDasharray = path.getTotalLength();
      path.style.strokeDashoffset = path.getTotalLength();
      path.style.transition = 'none';
      path.style.strokeOpacity = '1';
    });
    timeoutIds.current.push(setTimeout(() => {
      paths.forEach((path) => {
        path.style.transition = 'stroke-dashoffset 2.2s cubic-bezier(.77,0,.18,1)';
        path.style.strokeDashoffset = '0';
      });
    }, 100));
    timeoutIds.current.push(setTimeout(() => {
      paths.forEach((path) => {
        path.style.transition = 'fill-opacity 2.2s cubic-bezier(.77,0,.18,1)';
        path.style.fillOpacity = '1';
      });
    }, 2400));
    timeoutIds.current.push(setTimeout(() => {
      paths.forEach((path) => {
        path.style.transition = 'stroke-opacity 1.2s cubic-bezier(.77,0,.18,1)';
        path.style.strokeOpacity = '0';
      });
    }, 4800));
  };

  React.useEffect(() => {
    let intervalId;
    const startAnimation = () => {
      animateLogo();
      intervalId = setInterval(() => {
        animateLogo();
      }, 10000);
    };
    startAnimation();
    return () => {
      if (intervalId) clearInterval(intervalId);
      timeoutIds.current.forEach(id => clearTimeout(id));
      timeoutIds.current = [];
    };
  }, []);

  return (
    <span style={{display:'inline-block', position:'relative'}}>
      <IcfLogoRaw ref={logoRef} {...props} />
      <style>{`
        svg[aria-label], svg[aria-label] path {
          transition: none;
        }
      `}</style>
    </span>
  );
};

const Hero = ({ currentView, setCurrentView }) => {
  const [currentSlide, setCurrentSlide] = React.useState(0);
  const [isPlaying, setIsPlaying] = React.useState(true);
  const [isVisible, setIsVisible] = React.useState(false);

  const images = [espectacularImg, espectacularImg2, espectacularImg3];

  // Animación de entrada
  React.useEffect(() => {
    setIsVisible(true);
  }, []);

  // Auto-play funcionalidad
  React.useEffect(() => {
    if (!isPlaying) return;
    
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % images.length);
    }, 6000);

    return () => clearInterval(interval);
  }, [images.length, isPlaying]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const togglePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  return (
    <section id="inicio" className="relative min-h-screen flex items-center justify-center overflow-hidden bg-black">
      {/* Cargar fuente personalizada */}
      <style dangerouslySetInnerHTML={{ __html: styles }} />
      
      {/* Background con efecto parallax */}
      <div className="absolute inset-0 w-full h-full z-0">
        {images.map((img, idx) => (
          <div
            key={idx}
            className={`absolute inset-0 w-full h-full transition-all duration-1000 ease-in-out ${
              idx === currentSlide 
                ? 'opacity-150 scale-105' 
                : 'opacity-0 scale-100'
            }`}
          >
            <img
              src={img}
              alt={`Slide ${idx + 1}`}
              className="object-cover w-full h-full"
              style={{
                filter: 'brightness(0.7) contrast(1.3) saturate(0.8)',
                objectPosition: 'center center',
              }}
            />
            {/* Overlay dinámico que cambia con cada slide */}
            <div 
              className={`absolute inset-0 transition-all duration-1000 ${
                idx === 0 ? 'bg-gradient-to-br from-orange-900/60 via-black/70 to-yellow-900/40' :
                idx === 1 ? 'bg-gradient-to-bl from-red-900/50 via-black/80 to-orange-800/30' :
                'bg-gradient-to-tr from-yellow-900/40 via-black/75 to-red-900/50'
              }`} 
            />
          </div>
        ))}
      </div>

      {/* Partículas flotantes decorativas */}
      <div className="absolute inset-0 z-5 pointer-events-none">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-2 h-2 bg-yellow-400/30 rounded-full animate-float"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 10}s`,
              animationDuration: `${8 + Math.random() * 4}s`
            }}
          />
        ))}
      </div>

      {/* Contenido principal */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6">
        <div className="flex flex-col items-center justify-center min-h-screen text-center">
          
          {/* Badge superior */}
          <div className={`mb-8 transform transition-all duration-1000 delay-200 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}>

          </div>

          {/* Logo animado y texto "Alimentos con valor" */}
          <div className={`mb-6 flex flex-col items-center justify-center transform transition-all duration-1000 delay-400 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}>
            <IcfLogo
              className="w-40 h-40 md:w-56 md:h-56 lg:w-64 lg:h-64 object-contain transition-transform duration-300 hover:scale-105 p-2"
              style={{
                filter:
                  "drop-shadow(0 2px 8px rgba(0,0,0,0.75)) brightness(1.1) contrast(1.2)"
              }}
              aria-label="AVA Logo"
            />
            {/* <div className="mt-4 w-full flex justify-center">
              <h1
                className="text-2xl md:text-4xl lg:text-5xl font-black leading-none animate-pulse-glow"
                style={{
                  fontFamily: "'Brokenbrush', 'Arial', 'sans-serif'",
                  background: 'linear-gradient(90deg, #ea580c 0%, #eab308 50%, #f97316 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  textShadow: '0 0 2px #fff, 0 0 8px rgba(255,255,255,0.08)'
                }}
              >
                <TypewriterText text="ALIMENTOS CON VALOR" delay={200} speed={10} />
              </h1>
            </div> */}
          </div>

          {/* Descripción */}
          <div className={`mb-12 max-w-2xl transform transition-all duration-1000 delay-1000 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}>
            <p className="text-lg md:text-xl text-white/80 leading-relaxed">
              Más de 49 años llevando a tu mesa los mejores productos con la calidad 
              y frescura que tu familia merece.
            </p>
          </div>
        </div>
      </div>

      {/* Controles del carousel */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20">
        <div className="flex items-center space-x-4 bg-black/30 backdrop-blur-md rounded-full px-6 py-3 border border-white/20">
          {/* Play/Pause button */}
          <button
            onClick={togglePlayPause}
            className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-all duration-300"
          >
            {isPlaying ? (
              <Pause className="w-5 h-5 text-white" />
            ) : (
              <Play className="w-5 h-5 text-white ml-0.5" />
            )}
          </button>

          {/* Dots indicator */}
          <div className="flex space-x-2">
            {images.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  currentSlide === index 
                    ? 'bg-yellow-400 shadow-lg shadow-yellow-400/50 scale-125' 
                    : 'bg-white/40 hover:bg-white/60'
                }`}
              />
            ))}
          </div>

          {/* Progress bar */}
          <div className="w-16 h-1 bg-white/20 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-orange-400 to-yellow-400 rounded-full transition-all duration-300"
              style={{ width: `${((currentSlide + 1) / images.length) * 100}%` }}
            />
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-6 right-6 animate-bounce">
        <div className="w-6 h-10 border-2 border-white/40 rounded-full flex justify-center">
          <div className="w-1 h-3 bg-white/60 rounded-full mt-2 animate-pulse" />
        </div>
      </div>
    </section>
  );
};

// Componente para efecto typewriter
// const TypewriterText = ({ text, delay = 0, speed = 90 }) => {
//   const [displayText, setDisplayText] = React.useState('');
//   const [currentIndex, setCurrentIndex] = React.useState(0);

//   const SPEED = speed;

//   React.useEffect(() => {
//     const timeout = setTimeout(() => {
//       if (currentIndex < text.length) {
//         setDisplayText(prev => prev + text[currentIndex]);
//         setCurrentIndex(prev => prev + 1);
//       }
//     }, delay + currentIndex * SPEED);

//     return () => clearTimeout(timeout);
//   }, [currentIndex, text, delay, SPEED]);

//   return (
//     <span>
//       {displayText}
//       {currentIndex < text.length && <span className="animate-pulse">|</span>}
//     </span>
//   );
// };

const styles = `
  @font-face {
    font-family: 'Brokenbrush';
    src: url('${font}') format('truetype');
    font-weight: normal;
    font-style: normal;
    font-display: swap;
  }

  @keyframes float {
    0%, 100% {
      transform: translateY(0) rotate(0deg);
      opacity: 0.4;
    }
    25% {
      transform: translateY(-20px) rotate(90deg);
      opacity: 0.8;
    }
    50% {
      transform: translateY(-10px) rotate(180deg);
      opacity: 0.6;
    }
    75% {
      transform: translateY(-30px) rotate(270deg);
      opacity: 0.9;
    }
  }

  .animate-float {
    animation: float linear infinite;
  }

  @keyframes pulse-glow {
    0%, 100% {
      filter: drop-shadow(0 0 2px rgba(234, 179, 8, 0.18));
    }
    50% {
      filter: drop-shadow(0 0 6px rgba(234, 179, 8, 0.22)) drop-shadow(0 0 10px rgba(234, 88, 12, 0.12));
    }
  }

  .animate-pulse-glow {
    animation: pulse-glow 3s ease-in-out infinite;
  }

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .animate-slide-up {
    animation: slideUp 0.6s ease-out forwards;
  }
`;

export default Hero;